'use client'

/**
 * The homepage search box — instant, grouped results as you type (products
 * with photo, FIN and price; collections; guides; brands; tools; articles),
 * keyboard navigation, Enter opens the highlighted row or the full results
 * page. Backed by /api/search, which ranks in Postgres (search_site()).
 */
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import * as React from 'react'
import { SafeImage } from '@/components/ui/safe-image'
import { Icon } from './canvas/sprite'
import { useSearchOutcomes } from '@/hooks/use-search-outcomes'
import { loadSearchResponse } from '@/lib/search/request'

interface Hit {
  title: string
  url: string
  type: string
  image: string | null
  sub?: string | null
  price?: number | null
  fin?: string | null
}

const GROUP_ORDER = ['Product', 'Collection', 'Guide', 'Brand', 'Tool', 'Article']
const GROUP_LABEL: Record<string, string> = { Product: 'Products', Collection: 'Collections', Guide: 'Guides', Brand: 'Brands', Tool: 'Free tools', Article: 'Articles' }

export function SearchBox({ placeholder, autoFocus = false, size = 'lg' }: { placeholder: string; autoFocus?: boolean; size?: 'lg' | 'md' }) {
  const router = useRouter()
  const { recordSelection } = useSearchOutcomes()
  const [q, setQ] = React.useState('')
  const [hits, setHits] = React.useState<Hit[]>([])
  const [open, setOpen] = React.useState(false)
  const [active, setActive] = React.useState(-1)
  const [busy, setBusy] = React.useState(false)
  const seq = React.useRef(0)
  const rootRef = React.useRef<HTMLFormElement>(null)
  const listId = React.useId()
  const inputId = React.useId()
  const inputRef = React.useRef<HTMLInputElement>(null)
  // jsx-a11y/no-autofocus: focus is requested programmatically, only where a caller asks for it
  React.useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  React.useEffect(() => {
    const term = q.trim()
    if (term.length < 2) return
    const controller = new AbortController()
    const mine = ++seq.current
    const t = setTimeout(async () => {
      setBusy(true)
      try {
        const data = await loadSearchResponse<{ results?: Hit[] }>(term, 10, controller.signal)
        if (!data || mine !== seq.current) return
        setHits(data.results ?? [])
        setOpen(true)
        setActive(-1)
      } catch {
        if (mine === seq.current) setHits([])
      } finally {
        if (mine === seq.current) setBusy(false)
      }
    }, 160)
    return () => {
      clearTimeout(t)
      controller.abort()
      seq.current++
    }
  }, [q])

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const ordered = React.useMemo(() => {
    const groups = new Map<string, Hit[]>()
    for (const h of hits) groups.set(h.type, [...(groups.get(h.type) ?? []), h])
    const flat: Array<{ group: string; hit: Hit; first: boolean }> = []
    for (const g of GROUP_ORDER) for (const [i, hit] of (groups.get(g) ?? []).entries()) flat.push({ group: g, hit, first: i === 0 })
    return flat
  }, [hits])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (active >= 0 && ordered[active]) {
      recordSelection(q, hits.length, ordered[active].hit.url)
      router.push(ordered[active].hit.url)
      setOpen(false)
      return
    }
    if (q.trim()) router.push(`/search?q=${encodeURIComponent(q.trim())}`)
  }
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!open || ordered.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, ordered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, -1))
    }
  }
  const h = size === 'lg' ? 48 : 40

  return (
    // FRIDGE-776 (owner 09/04/2026: "search button should be next to search bar, not under"):
    // ONE row — the form fills its slot, the input grows from a zero basis, the button is
    // the trailing element at the input's height. Only below 400 px does the stylesheet
    // (homepage.css `.srch-form`) let the button drop to a full-width row under the input.
    <form ref={rootRef} action="/search" method="get" onSubmit={submit} role="search" className="srch-form ph-no-capture" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
      <label htmlFor={inputId} style={{ flex: '1 1 0%', display: 'flex', alignItems: 'center', height: h, background: '#ffffff', border: `1px solid ${open ? '#ff9951' : '#d5dbe3'}`, boxShadow: open ? '0 0 0 3px rgba(255, 153, 81, 0.18)' : undefined, borderRadius: 10, padding: '0 16px', gap: 10, minWidth: 0, transition: 'border-color 0.15s ease, box-shadow 0.15s ease' }}>
        <Icon id="i-search" className="i" style={{ color: '#98a2b3' }} />
        <input
          id={inputId}
          ref={inputRef}
          name="q"
          type="search"
          value={q}
          onChange={(e) => {
            const v = e.target.value
            setQ(v)
            if (v.trim().length < 2) {
              seq.current++
              setHits([])
              setOpen(false)
              setBusy(false)
            }
          }}
          onFocus={() => hits.length && setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          aria-label="Search Fridge.com"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          autoComplete="off"
          style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 14, color: '#101828', background: 'transparent' }}
        />
        {busy ? <span className="livedot" style={{ background: '#ff9951' }} aria-hidden="true" /> : null}
      </label>
      <button type="submit" className="srch-btn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: h, padding: '0 22px', background: '#ff9951', color: '#101828', fontSize: 14, fontWeight: 700, borderRadius: 10, whiteSpace: 'nowrap', border: 'none', cursor: 'pointer' }}>
        Search
      </button>
      {open && ordered.length > 0 ? (
        <div id={listId} role="listbox" className="srch-panel">
          {ordered.map(({ group, hit, first }, i) => (
            <React.Fragment key={`${group}-${hit.url}`}>
              {first ? <div className="srch-group">{GROUP_LABEL[group] ?? group}</div> : null}
              <Link
                href={hit.url}
                role="option"
                aria-selected={i === active}
                className={`srch-row${i === active ? ' srch-row-on' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => { recordSelection(q, hits.length, hit.url); setOpen(false) }}
              >
                {group === 'Product' || group === 'Brand' ? (
                  <span className="srch-th">
                    {hit.image ? <SafeImage src={hit.image} alt="" width={44} height={44} className="object-contain max-w-full max-h-full" /> : null}
                  </span>
                ) : (
                  <span className="srch-th srch-th-ico">
                    <Icon id={group === 'Collection' ? 'i-tag' : group === 'Guide' ? 'i-book' : group === 'Tool' ? 'i-cpu' : 'i-book'} className="i" style={{ color: '#0469f0' }} />
                  </span>
                )}
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, gap: 2 }}>
                  <span className="srch-t">{hit.title}</span>
                  {hit.sub || hit.fin ? (
                    <span className="srch-s">
                      {hit.fin ? <span className="idchip">{hit.fin}</span> : null}
                      {hit.fin && hit.sub ? ' · ' : ''}
                      {hit.sub}
                    </span>
                  ) : null}
                </span>
                {typeof hit.price === 'number' && hit.price > 0 ? <span className="srch-p">{`$${Math.round(hit.price / 100).toLocaleString('en-US')}`}</span> : null}
              </Link>
            </React.Fragment>
          ))}
          <Link href={`/search?q=${encodeURIComponent(q.trim())}`} className="srch-all" onClick={() => setOpen(false)}>
            See all results for “{q.trim()}” →
          </Link>
        </div>
      ) : null}
    </form>
  )
}
